import './styles.css'

import { LoadingOutlined } from '@ant-design/icons'
import { Button, type ButtonProps as AntButtonProps } from 'antd'
import clsx from 'clsx'
import type { ReactNode } from 'react'

import { ChevronLeftIcon } from '../Icons'

type IconComponent = React.ComponentType<React.PropsWithChildren<React.SVGProps<SVGSVGElement>>>

type IconProp = IconComponent | ReactNode

type ExtendedButtonType = 'primary' | 'default' | 'dashed' | 'link' | 'text' | 'ghost' | 'action'

interface CustomButtonProps extends Omit<AntButtonProps, 'type' | 'onClick' | 'icon'> {
  children?: ReactNode
  isQuantity?: boolean
  isPriceBox?: boolean
  onClick?: () => void
  disabled?: boolean
  className?: string
  type?: ExtendedButtonType
  isInverted?: boolean
  buttonSize?: 'default' | 'small' | 'large'
  isPriceEqualZero?: boolean
  loading?: boolean
  icon?: IconProp
}

const isIconComponent = (icon: IconProp): icon is IconComponent => {
  return typeof icon === 'function'
}

const CustomButton = ({
  children,
  isQuantity = false,
  isPriceBox = false,
  onClick = () => {},
  disabled = false,
  className = '',
  type = 'primary',
  isInverted = false,
  buttonSize = 'default',
  isPriceEqualZero: _isPriceEqualZero,
  loading = false,
  icon: iconProp,
  ...rest
}: CustomButtonProps) => {
  const classNames = clsx('custom-button', className, {
    'custom-button--quantity': isQuantity,
    'custom-button--price-box': isPriceBox,
    'custom-button--small': buttonSize === 'small',
    'custom-button--large': buttonSize === 'large',
    'custom-button--loading': loading,
    'custom-button--inverted': isInverted,
  })

  const iconSizeClass = `custom-button__icon-wrapper`

  const renderIcon = () => {
    if (loading) {
      return (
        <span className={iconSizeClass}>
          <LoadingOutlined />
        </span>
      )
    }

    if (iconProp) {
      if (isIconComponent(iconProp)) {
        const Icon = iconProp
        return (
          <span className={iconSizeClass}>
            <Icon />
          </span>
        )
      }

      return (
        <span className={iconSizeClass}>
          {iconProp}
        </span>
      )
    }

    return null
  }

  return (
    <Button
      {...rest}
      type={type as AntButtonProps['type']}
      className={classNames}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {renderIcon()}
      {children}
    </Button>
  )
}

export const BackButton = ({ children, ...props }: Omit<CustomButtonProps, 'icon'>) => {
  return (
    <CustomButton icon={ChevronLeftIcon} {...props}>
      {children}
    </CustomButton>
  )
}

export default CustomButton
